import { Dispatch, SetStateAction, useMemo } from 'react';
import * as R from 'ramda';

import AssigneeFilter from '@atom/components/common/assigneeFilter/AssigneeFilter';
import InputDateRange from '@atom/components/common/inputDateRange/InputDateRange';
import UsersFilter from '@atom/components/common/usersFilter/UsersFilter';
import { Select, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { AssetDetail } from '@atom/types/asset';
import {
  AssetRequestsFilters,
  DescriptionOption,
} from '@atom/types/assetRequest';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import {
  convertRangeDateToMillis,
  DateRange,
  DateRangeTerminator,
} from '@atom/utilities/workOrdersDateFilterUtilities';

import {
  DESCRIPTION_LABELS,
  DESCRIPTION_OPTIONS,
  STATUS_LABELS,
} from '../assetRequestConstants';

import { getADOTStatusOptions } from './adotFilterUtilities';

import classes from './adotFilters.module.css';

const { MenuItem } = Select;

const styles = {
  input: {
    marginTop: '1rem',
  },
  label: {
    color: colors.neutral.dim,
  },
};

const inputLabelSx = {
  color: `${colors.neutral.dim} !important`,
};

type ADOTInventoryTransferFiltersProps = {
  filtersCart: AssetRequestsFilters;
  setFiltersCart: Dispatch<SetStateAction<AssetRequestsFilters>>;
  asset?: AssetDetail;
  createdPresetSelected?: DateRange;
  setCreatedPresetSelect?: Dispatch<SetStateAction<DateRange>>;
  updatedPresetSelected?: DateRange;
  setUpdatedPresetSelect?: Dispatch<SetStateAction<DateRange>>;
};

const ADOTInventoryTransferFilters = ({
  filtersCart,
  setFiltersCart,
  asset,
  createdPresetSelected,
  setCreatedPresetSelect,
  updatedPresetSelected,
  setUpdatedPresetSelect,
}: ADOTInventoryTransferFiltersProps) => {
  const updateFilter = (value: any, property: keyof AssetRequestsFilters) => {
    setFiltersCart(prev => ({ ...prev, [property]: value }));
  };

  const getDateValue = (property: keyof AssetRequestsFilters) => {
    if (!isNilOrEmpty(filtersCart[property])) {
      return new Date(Number(filtersCart[property]));
    }

    return null;
  };

  const handleDateChange =
    (
      property: keyof AssetRequestsFilters,
      terminator: DateRangeTerminator,
      preset?: DateRange,
    ) =>
    (val: Date) => {
      updateFilter(convertRangeDateToMillis(val, terminator, preset), property);
    };

  const handleDescriptionUpdate = event => {
    const option = event.target.value;

    const updatedFilters = {
      [DescriptionOption.ALL]: {
        assetId: asset?.id,
        fromAssetId: asset?.id,
      },
      [DescriptionOption.OUTGOING]: {
        assetId: asset?.id,
        fromAssetId: null,
      },
      [DescriptionOption.INCOMING]: {
        assetId: null,
        fromAssetId: asset?.id,
      },
    };

    setFiltersCart(prev => ({ ...prev, ...updatedFilters[option] }));
  };

  const handleAssigneesChange = (userIds: string[], groupIds: string[]) => {
    setFiltersCart(prev => ({
      ...prev,
      assignedTo: userIds,
      assignedToGroups: groupIds,
    }));
  };

  const descriptionValue = useMemo(() => {
    switch (true) {
      case !isNilOrEmpty(filtersCart.assetId) &&
        !isNilOrEmpty(filtersCart.fromAssetId): {
        return DescriptionOption.ALL;
      }
      case !isNilOrEmpty(filtersCart.assetId) &&
        isNilOrEmpty(filtersCart.fromAssetId): {
        return DescriptionOption.OUTGOING;
      }
      case isNilOrEmpty(filtersCart.assetId) &&
        !isNilOrEmpty(filtersCart.fromAssetId): {
        return DescriptionOption.INCOMING;
      }
      default:
        return '';
    }
  }, [filtersCart.assetId, filtersCart.fromAssetId]);

  const statusOptions = getADOTStatusOptions(filtersCart.type);
  const showDescriptionFilter = !isNilOrEmpty(asset);

  return (
    <>
      <div className={classes.inputContainer}>
        <TextField
          style={styles.input}
          slotProps={{ inputLabel: { style: styles.label } }}
          type="number"
          value={filtersCart.name || ''}
          placeholder="Enter number only"
          label="Request ID"
          onChange={event => updateFilter(String(event.target.value), 'name')}
          data-cy="adotRequestsFiltersRequestIdInput"
        />
      </div>
      <div className={classes.inputContainer}>
        <Select
          key="status"
          multiple
          displayEmpty
          label="Status"
          value={filtersCart?.statuses || []}
          onChange={event => updateFilter(event.target.value, 'statuses')}
          renderValue={R.length(filtersCart?.statuses) > 0 ? null : () => 'All'}
          inputLabelSx={inputLabelSx}
          data-cy="adotRequestsFiltersStatusSelect"
        >
          {statusOptions.map(status => (
            <MenuItem key={status} value={status}>
              {STATUS_LABELS[status]}
            </MenuItem>
          ))}
        </Select>
      </div>
      {showDescriptionFilter && (
        <div className={classes.inputContainer}>
          <Select
            key="description"
            label="Description"
            InputLabelProps={{ style: styles.label }}
            fullWidth
            onChange={event => handleDescriptionUpdate(event)}
            value={descriptionValue}
            data-cy="adotRequestsFiltersDescriptionSelect"
          >
            {DESCRIPTION_OPTIONS.map((option: string) => (
              <MenuItem key={option} value={option}>
                {DESCRIPTION_LABELS[option]}
              </MenuItem>
            ))}
          </Select>
        </div>
      )}
      <div className={classes.inputContainer}>
        <AssigneeFilter
          userIds={filtersCart?.assignedTo || []}
          groupIds={filtersCart?.assignedToGroups || []}
          onChange={handleAssigneesChange}
          dataCy="adotRequestsFiltersAssignedTo"
        />
      </div>
      <div className={classes.inputContainer}>
        <UsersFilter
          value={filtersCart?.createdBy}
          updateValue={newValue => updateFilter(newValue, 'createdBy')}
          placeholder="Search users"
          label="Created By"
          showTitle
          dataCy="adotRequestsFiltersCreatedBy"
        />
      </div>
      <div className={classes.inputContainer}>
        <InputDateRange
          label="Created Date"
          presetSelected={createdPresetSelected}
          startDateValue={getDateValue('createdDateStart')}
          handleStartChange={handleDateChange(
            'createdDateStart',
            DateRangeTerminator.START,
            createdPresetSelected,
          )}
          endDateValue={getDateValue('createdDateEnd')}
          handleEndChange={handleDateChange(
            'createdDateEnd',
            DateRangeTerminator.END,
            createdPresetSelected,
          )}
          handleSelectDatePreset={selected =>
            setCreatedPresetSelect(selected?.value)
          }
        />
      </div>
      <div className={classes.inputContainer}>
        <InputDateRange
          label="Resolution Date"
          presetSelected={updatedPresetSelected}
          startDateValue={getDateValue('resolutionDateStart')}
          handleStartChange={handleDateChange(
            'resolutionDateStart',
            DateRangeTerminator.START,
            updatedPresetSelected,
          )}
          endDateValue={getDateValue('resolutionDateEnd')}
          handleEndChange={handleDateChange(
            'resolutionDateEnd',
            DateRangeTerminator.END,
            updatedPresetSelected,
          )}
          handleSelectDatePreset={selected =>
            setUpdatedPresetSelect(selected?.value)
          }
        />
      </div>
    </>
  );
};

export default ADOTInventoryTransferFilters;
